/**
 * Minimal, XSS-safe Markdown → HTML for product docs.
 * Supports: ATX headings, paragraphs, lists, fenced code, tables, blockquotes,
 * inline code, links, emphasis (** / * / _), and hard line breaks.
 * Raw HTML in source is escaped (never executed).
 */

const ESCAPE_MAP: Record<string, string> = {
  "&": "&amp;",
  "<": "&lt;",
  ">": "&gt;",
  '"': "&quot;",
  "'": "&#39;",
};

export function escapeHtml(text: string): string {
  return text.replace(/[&<>"']/g, (ch) => ESCAPE_MAP[ch] ?? ch);
}

/** Allow relative site paths, hashes, http(s), and mailto only. */
export function sanitizeHref(href: string): string {
  const t = href.trim();
  if (!t) return "#";
  if (t.startsWith("/") || t.startsWith("#")) return t;
  if (/^https?:\/\//i.test(t)) return t;
  if (/^mailto:/i.test(t)) return t;
  return "#";
}

function renderInline(text: string): string {
  // Protect inline code first with placeholders
  const codes: string[] = [];
  let s = text.replace(/`([^`\n]+)`/g, (_, code: string) => {
    const i = codes.length;
    codes.push(`<code>${escapeHtml(code)}</code>`);
    return `\u0000C${i}\u0000`;
  });

  // Links: [label](url)
  s = s.replace(/\[([^\]]+)\]\(([^)\s]+)(?:\s+"[^"]*")?\)/g, (_, label: string, url: string) => {
    const href = sanitizeHref(url);
    return `<a href="${escapeHtml(href)}">${escapeHtml(label)}</a>`;
  });

  // Bold then italic (order matters)
  s = s.replace(/\*\*([^*]+)\*\*/g, (_, inner: string) => `<strong>${escapeHtml(inner)}</strong>`);
  s = s.replace(/__([^_]+)__/g, (_, inner: string) => `<strong>${escapeHtml(inner)}</strong>`);
  s = s.replace(/(?<!\*)\*([^*\n]+)\*(?!\*)/g, (_, inner: string) => `<em>${escapeHtml(inner)}</em>`);
  s = s.replace(/(?<!_)_([^_\n]+)_(?!_)/g, (_, inner: string) => `<em>${escapeHtml(inner)}</em>`);

  // Remaining plain text segments still need escaping; placeholders and tags already built.
  // Split on our placeholders / simple tags we inserted and escape the rest.
  const parts = s.split(/(\u0000C\d+\u0000|<a href="[^"]*">[^<]*<\/a>|<strong>[^<]*<\/strong>|<em>[^<]*<\/em>)/g);
  s = parts
    .map((part) => {
      if (!part) return "";
      const ph = part.match(/^\u0000C(\d+)\u0000$/);
      if (ph) return codes[Number(ph[1])] ?? "";
      if (part.startsWith("<a ") || part.startsWith("<strong>") || part.startsWith("<em>")) {
        return part;
      }
      return escapeHtml(part);
    })
    .join("");

  return s;
}

type Block =
  | { type: "heading"; level: number; text: string }
  | { type: "paragraph"; text: string }
  | { type: "ul"; items: string[] }
  | { type: "ol"; items: string[] }
  | { type: "code"; lang: string; code: string }
  | { type: "table"; headers: string[]; rows: string[][] }
  | { type: "blockquote"; text: string }
  | { type: "hr" };

export type MarkdownHeading = {
  level: number;
  text: string;
  id: string;
};

/**
 * Create a stable, readable anchor id for a markdown heading.
 * Duplicate headings receive a numeric suffix in document order.
 */
export function slugifyHeading(
  text: string,
  usedIds: Set<string> = new Set(),
): string {
  const plain = text
    .replace(/`([^`]+)`/g, "$1")
    .replace(/\[([^\]]+)\]\([^)]*\)/g, "$1")
    .replace(/[\*_~]/g, "")
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
  const base = plain || "section";
  let candidate = base;
  let suffix = 2;
  while (usedIds.has(candidate)) {
    candidate = `${base}-${suffix}`;
    suffix += 1;
  }
  usedIds.add(candidate);
  return candidate;
}

function splitTableRow(line: string): string[] {
  let value = line.trim();
  if (value.startsWith("|")) value = value.slice(1);
  if (value.endsWith("|")) value = value.slice(0, -1);
  return value.split(/(?<!\\)\|/g).map((cell) => cell.replace(/\\\|/g, "|").trim());
}

function isTableDelimiter(line: string): boolean {
  const cells = splitTableRow(line);
  return (
    cells.length > 0 &&
    cells.every((cell) => /^:?-{3,}:?$/.test(cell.trim()))
  );
}

function parseBlocks(md: string): Block[] {
  const normalized = md.replace(/\r\n/g, "\n");
  const lines = normalized.split("\n");
  const blocks: Block[] = [];
  let i = 0;

  while (i < lines.length) {
    const line = lines[i] ?? "";

    // Fenced code
    const fence = line.match(/^```([\w-]*)\s*$/);
    if (fence) {
      const lang = fence[1] ?? "";
      i += 1;
      const body: string[] = [];
      while (i < lines.length && !/^\s*```\s*$/.test(lines[i] ?? "")) {
        body.push(lines[i] ?? "");
        i += 1;
      }
      if (i < lines.length) i += 1; // closing fence
      blocks.push({ type: "code", lang, code: body.join("\n") });
      continue;
    }

    if (/^\s*---+\s*$/.test(line) || /^\s*\*\*\*+\s*$/.test(line)) {
      blocks.push({ type: "hr" });
      i += 1;
      continue;
    }

    // Pipe tables. Keep this intentionally small but useful for the public
    // guides: a header row, a delimiter row, and any number of body rows.
    if (
      line.includes("|") &&
      i + 1 < lines.length &&
      isTableDelimiter(lines[i + 1] ?? "")
    ) {
      const headers = splitTableRow(line);
      i += 2;
      const rows: string[][] = [];
      while (i < lines.length && (lines[i] ?? "").includes("|")) {
        const row = splitTableRow(lines[i] ?? "");
        if (row.length > 0) rows.push(row);
        i += 1;
      }
      blocks.push({ type: "table", headers, rows });
      continue;
    }

    const heading = line.match(/^(#{1,6})\s+(.+?)\s*#*\s*$/);
    if (heading) {
      blocks.push({
        type: "heading",
        level: heading[1]!.length,
        text: heading[2]!.trim(),
      });
      i += 1;
      continue;
    }

    // Unordered list
    if (/^\s*[-*+]\s+/.test(line)) {
      const items: string[] = [];
      while (i < lines.length && /^\s*[-*+]\s+/.test(lines[i] ?? "")) {
        items.push((lines[i] ?? "").replace(/^\s*[-*+]\s+/, ""));
        i += 1;
      }
      blocks.push({ type: "ul", items });
      continue;
    }

    // Ordered list
    if (/^\s*\d+\.\s+/.test(line)) {
      const items: string[] = [];
      while (i < lines.length && /^\s*\d+\.\s+/.test(lines[i] ?? "")) {
        items.push((lines[i] ?? "").replace(/^\s*\d+\.\s+/, ""));
        i += 1;
      }
      blocks.push({ type: "ol", items });
      continue;
    }

    // Simple blockquotes are used for product notices and safety guidance.
    if (/^\s*>\s?/.test(line)) {
      const quoted: string[] = [];
      while (i < lines.length && /^\s*>\s?/.test(lines[i] ?? "")) {
        quoted.push((lines[i] ?? "").replace(/^\s*>\s?/, ""));
        i += 1;
      }
      blocks.push({ type: "blockquote", text: quoted.join("\n") });
      continue;
    }

    // Blank
    if (/^\s*$/.test(line)) {
      i += 1;
      continue;
    }

    // Paragraph (merge consecutive non-blank, non-special lines)
    const para: string[] = [];
    while (
      i < lines.length &&
      !/^\s*$/.test(lines[i] ?? "") &&
      !/^(#{1,6})\s+/.test(lines[i] ?? "") &&
      !/^\s*[-*+]\s+/.test(lines[i] ?? "") &&
      !/^\s*\d+\.\s+/.test(lines[i] ?? "") &&
      !/^```/.test(lines[i] ?? "") &&
      !/^\s*---+\s*$/.test(lines[i] ?? "") &&
      !/^\s*>\s?/.test(lines[i] ?? "") &&
      !(
        (lines[i] ?? "").includes("|") &&
        i + 1 < lines.length &&
        isTableDelimiter(lines[i + 1] ?? "")
      )
    ) {
      para.push((lines[i] ?? "").trimEnd());
      i += 1;
    }
    blocks.push({ type: "paragraph", text: para.join("\n") });
  }

  return blocks;
}

function blocksToHtml(blocks: Block[]): string {
  const usedHeadingIds = new Set<string>();
  return blocks
    .map((b) => {
      switch (b.type) {
        case "heading": {
          const tag = `h${Math.min(6, Math.max(1, b.level))}`;
          const id = slugifyHeading(b.text, usedHeadingIds);
          // Keep the heading markup minimal for readable HTML while placing
          // the anchor immediately before it for sticky-header scroll offset.
          return `<span id="${escapeHtml(id)}" class="docs-heading-anchor" aria-hidden="true"></span><${tag}>${renderInline(b.text)}</${tag}>`;
        }
        case "paragraph":
          return `<p>${renderInline(b.text).replace(/\n/g, "<br />\n")}</p>`;
        case "ul":
          return `<ul>${b.items.map((item) => `<li>${renderInline(item)}</li>`).join("")}</ul>`;
        case "ol":
          return `<ol>${b.items.map((item) => `<li>${renderInline(item)}</li>`).join("")}</ol>`;
        case "code": {
          const langClass = b.lang ? ` class="language-${escapeHtml(b.lang)}"` : "";
          const language = b.lang.trim() || "code";
          return `<div class="docs-code-block" data-language="${escapeHtml(language)}"><div class="docs-code-toolbar"><span class="docs-code-language">${escapeHtml(language)}</span><button type="button" class="docs-code-copy" data-code-copy aria-label="Copy ${escapeHtml(language)} code"><span>Copy</span></button></div><pre><code${langClass}>${escapeHtml(b.code)}</code></pre></div>`;
        }
        case "table": {
          const cell = (value: string, tag: "th" | "td") =>
            `<${tag}>${renderInline(value)}</${tag}>`;
          return `<div class="docs-table-wrap"><table class="docs-table"><thead><tr>${b.headers.map((header) => cell(header, "th")).join("")}</tr></thead><tbody>${b.rows
            .map(
              (row) =>
                `<tr>${b.headers
                  .map((_, index) => cell(row[index] ?? "", "td"))
                  .join("")}</tr>`,
            )
            .join("")}</tbody></table></div>`;
        }
        case "blockquote":
          return `<blockquote>${renderInline(b.text).replace(/\n/g, "<br />\n")}</blockquote>`;
        case "hr":
          return "<hr />";
        default:
          return "";
      }
    })
    .join("\n");
}

/** Extract headings for an on-page table of contents without re-parsing in the UI. */
export function extractMarkdownHeadings(markdown: string): MarkdownHeading[] {
  const usedHeadingIds = new Set<string>();
  return parseBlocks(markdown)
    .filter((block): block is Extract<Block, { type: "heading" }> => block.type === "heading")
    .map((heading) => ({
      level: heading.level,
      text: heading.text,
      id: slugifyHeading(heading.text, usedHeadingIds),
    }));
}

/** Convert markdown source to safe HTML (no raw HTML pass-through). */
export function renderMarkdownToSafeHtml(markdown: string): string {
  if (!markdown.trim()) return "";
  return blocksToHtml(parseBlocks(markdown));
}
